import { useUserStore } from '../../../../stores/userStore';
import type {
  WorkOrderViewModel,
  WorkOrderDetailViewModel,
  GlobalVariableViewModel
} from '@api/enterprise/openapi/models';
import {
  useWorkOrder,
  useWorkOrderDetails
} from '@api/enterprise/workorder/queries';
import { useAllowedEventTypes } from '@api/enterprise/eventType';
import { filterWorkOrderDetails } from '@api/enterprise/workorder/utils/filterWorkOrderDetails';
import { useOfflineDownloadService } from '@services/offlineDownloadService';
import { useState, useEffect } from 'react';
import useOnlineStatus from '@hooks/useOnlineStatus';

type UseWorkOrderDetailDataProps = {
  jobNumber: string;
};

export const useWorkOrderDetailData = ({
  jobNumber
}: UseWorkOrderDetailDataProps) => {
  const { isAuthenticated } = useUserStore();
  const isOnline = useOnlineStatus();
  const {
    getCachedWorkOrder,
    getCachedWorkOrderDetails,
    getCachedGlobalVariable
  } = useOfflineDownloadService();

  // Get allowed event types loading state for online path
  const { isLoading: isLoadingEvents } = useAllowedEventTypes();

  // Online queries - use API when online
  const onlineWorkOrderQuery = useWorkOrder(
    jobNumber,
    isAuthenticated && isOnline && !!jobNumber
  );

  const onlineDetailsQuery = useWorkOrderDetails(
    jobNumber,
    isAuthenticated && isOnline && !!jobNumber
  );

  // Offline state for cached data
  const [cachedWorkOrder, setCachedWorkOrder] =
    useState<WorkOrderViewModel | null>(null);
  const [cachedDetails, setCachedDetails] = useState<
    WorkOrderDetailViewModel[]
  >([]);
  const [offlineLoading, setOfflineLoading] = useState(!isOnline);
  const [offlineError, setOfflineError] = useState<string | null>(null);

  // Fetch cached data only when offline
  useEffect(() => {
    const fetchCachedData = async () => {
      if (!isOnline && jobNumber && isAuthenticated) {
        setOfflineLoading(true);
        setOfflineError(null);
        try {
          const workOrder = await getCachedWorkOrder(jobNumber);
          const rawDetails = await getCachedWorkOrderDetails(jobNumber);

          // Read allowed events directly from Dexie to avoid TanStack Query fallback race
          let offlineAllowedEvents: string[] | null = null;
          try {
            const cachedVar = (await getCachedGlobalVariable(
              'HIDE_ALLOWED_EVENT_TYPES'
            )) as GlobalVariableViewModel | null;
            if (cachedVar?.blobText) {
              offlineAllowedEvents = JSON.parse(cachedVar.blobText);
            }
          } catch {
            // If we can't read the global variable, don't filter
          }

          // Only filter if we have the actual allowed events from Dexie;
          // if not cached, show all details rather than filtering to just CALIBRATION
          const filteredDetails = offlineAllowedEvents
            ? filterWorkOrderDetails(rawDetails, offlineAllowedEvents)
            : rawDetails;

          setCachedWorkOrder(workOrder);
          setCachedDetails(filteredDetails);
        } catch (err) {
          setOfflineError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
          setOfflineLoading(false);
        }
      }
    };

    fetchCachedData();
  }, [
    jobNumber,
    isAuthenticated,
    isOnline,
    getCachedWorkOrder,
    getCachedWorkOrderDetails,
    getCachedGlobalVariable
  ]);

  // Return appropriate data based on online status
  if (isOnline) {
    // When online, use API data
    return {
      data:
        onlineWorkOrderQuery.data && onlineDetailsQuery.data
          ? {
              workOrder: onlineWorkOrderQuery.data,
              details: onlineDetailsQuery.data
            }
          : null,
      workOrder: onlineWorkOrderQuery.data || null,
      details: onlineDetailsQuery.data || [],
      isLoading:
        onlineWorkOrderQuery.isLoading ||
        onlineDetailsQuery.isLoading ||
        isLoadingEvents,
      error:
        onlineWorkOrderQuery.error?.message ||
        onlineDetailsQuery.error?.message ||
        null
    };
  } else {
    // When offline, use cached data
    return {
      data:
        cachedWorkOrder && cachedDetails
          ? { workOrder: cachedWorkOrder, details: cachedDetails }
          : null,
      workOrder: cachedWorkOrder,
      details: cachedDetails,
      isLoading: offlineLoading,
      error: offlineError
    };
  }
};
